import type { API } from 'chessboard/api/config/types';
import { Game } from 'chessboard/model/game/types';
import { removeUnsetHeaders } from 'chessboard/api/model/remove-unset-headers';
import { Core } from 'chessboard/core/config/types';
import { getColorAsWord } from 'chessboard/model/game/get-color-as-word';
import { StringUtil } from '@chesscom/web-ui';
import { Translator } from 'utils/dummy-translator';
import { getOppositeColor } from 'chessboard/model/game/get-opposite-color';
import JCE from '@chesscom/js-chess-engine';
import { MoveList } from 'chessboard/model/move-list/types';

const { capitalizeFirstLetter } = StringUtil;

/**
 * Create the outOfTime method.
 */
export function createOutOfTime(context: API.Context) {
  /**
   * Set the game details (saves to PGN, which is the source of truth).
   */
  return function outOfTime(color: Game.ColorsAsNumbers): API.Response {
    const { api, game, respond } = context;
    const winner = getOppositeColor(color);
    const { Result, Termination } = !insufficientMaterial(color)
      ? getWinnerDetails()
      : getInsufficientDetails();

    api.setGameDetails({ Result, Termination });

    game.header({ Termination });

    return respond(Core.APIEvents.OutOfTime, {
      ...removeUnsetHeaders(game.header()),
      color,
    });

    /**
     * Get winner details
     */
    function getWinnerDetails(): { Result: Game.Results; Termination: string } {
      const colorAsWord = capitalizeFirstLetter(getColorAsWord(winner));
      const winningUserName = api.getHeaders()[colorAsWord];

      return {
        Result: winner === Game.ColorsAsNumbers.Black ? Game.Results.Black : Game.Results.White,
        Termination: Translator.trans('%username% won on time', { '%username': winningUserName }),
      };
    }

    /**
     * Get insufficient details
     */
    function getInsufficientDetails(): { Result: Game.Results; Termination: string } {
      return {
        Result: Game.Results.Draw,
        Termination: MoveList.Translation.GameTerminationMessages.TimeoutVsInsufficientMaterial,
      };
    }
  };

  /**
   * has sufficient material to win
   */
  function insufficientMaterial(color: Game.ColorsAsNumbers): boolean {
    const regex = '[a-jl-z]'; // all pieces but the king
    const fen = context.api
      .getFEN()
      .replace(
        new RegExp(color === Game.ColorsAsNumbers.Black ? regex : regex.toUpperCase(), 'g'),
        '1',
      );

    return (JCE.createGame({ fen, analysis: true }) as JCE.Game).insufficientMaterial();
  }
}
